import type { ComponentType, ReactNode } from "react";
import Link from "next/link";
import { Inbox, Info } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { SectionTitle, Muted } from "@/components/ui/typography";
import { cn } from "@/lib/utils";

type Action = { label: string; onClick: () => void } | { label: string; href: string };

/**
 * The one "nothing here" state, in two sizes.
 *
 *   page   (default) a whole view with nothing to list: a blank table reads as loading or broken, so say which, and offer the way
 *          back from a filter. Centred in the space given, with an icon, a title, a line and an action.
 *   note   an empty list inside a tab or a card: one info card, "No records" (or `title`) and why, which is `children`.
 *
 * Draws on the server as well as in the browser: an action that goes somewhere is a link (`href`), one that does something is a
 * handler (`onClick`, from a client component).
 */
export function EmptyState(
  props:
    | {
        size?: "page";
        icon?: ComponentType<{ className?: string; strokeWidth?: number }>;
        title: string;
        description?: ReactNode;
        action?: Action;
        className?: string;
      }
    | { size: "note"; title?: string; children: ReactNode }
) {
  if (props.size === "note") {
    return (
      <Alert variant="info">
        <Info aria-hidden />
        <AlertTitle>{props.title ?? "No records"}</AlertTitle>
        <AlertDescription>{props.children}</AlertDescription>
      </Alert>
    );
  }
  const { icon: Icon = Inbox, title, description, action, className } = props;
  return (
    <div
      className={cn(
        // Centred in the space given; no border or card.
        "flex min-h-[60dvh] flex-1 flex-col items-center justify-center gap-4 px-6 py-16 text-center",
        className
      )}
    >
      {/* The same green as a good state: an empty list is not a fault. */}
      <span className="flex size-24 items-center justify-center rounded-full bg-readiness-ready-bg text-readiness-ready-fg">
        <Icon className="size-11" strokeWidth={1.25} />
      </span>
      <SectionTitle as="div">{title}</SectionTitle>
      {description ? <Muted className="max-w-sm leading-relaxed">{description}</Muted> : null}
      {action && "href" in action ? (
        <Button variant="outline" size="sm" render={<Link href={action.href} />} nativeButton={false} className="mt-1">
          {action.label}
        </Button>
      ) : action ? (
        <Button variant="outline" size="sm" onClick={action.onClick} className="mt-1">
          {action.label}
        </Button>
      ) : null}
    </div>
  );
}
